<template>
  <CCard v-if="hasMedia" class="mb-3">
    <CCardBody class="p-3">
      <MediaGrid
        :items="orderMedia.items"
        :loading="orderMedia.loading"
        :pagination="orderMedia.pagination"
        :selected="selectedMediaIds"
        :context-actions="contextActions"
        :collapsible="true"
        :collapsed-rows="2"
        :infinite-scroll="true"
        @update:selected="selectedMediaIds = $event"
        @load-more="loadMore"
        @action="onAction"
      >
        <template #selection-actions="{ selected, clear }">
          <CButton
            v-if="canManagePhotos"
            color="danger"
            size="sm"
            @click="removeSelected"
          >
            <CIcon name="cil-trash" size="sm" class="mr-1"/>
            <translate translate-context="accounting.orderItem">Remove</translate>
          </CButton>
          <CButton
            v-if="canManagePhotos && hasSelectedWithStatus"
            color="warning"
            size="sm"
            @click="clearStatusSelected"
          >
            <CIcon name="cil-x-circle" size="sm" class="mr-1"/>
            <translate translate-context="accounting.orderItem">Clear status</translate>
          </CButton>
          <CButton color="secondary" size="sm" variant="ghost" @click="clear">
            <translate translate-context="accounting.orderItem">Clear</translate>
          </CButton>
        </template>

        <!-- Photo status overlay -->
        <template #item-overlay="{ item }">
          <div
            v-if="isRemoved(item.id)"
            class="photo-status-badge photo-status-remove"
            :title="$pgettext('accounting.orderItem', 'Marked for removal')"
          >
            <CIcon name="cipTrashFull" size="sm"/>
          </div>
        </template>
      </MediaGrid>
    </CCardBody>
  </CCard>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import MediaGrid from '@/domain/core/components/MediaGrid.vue'

export default {
  name: 'MovementOrderItemCard',
  components: {
    MediaGrid
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    currency: {
      type: String,
      default: 'EUR'
    },
    canManagePhotos: {
      type: Boolean,
      default: false
    },
    removedPhotoIds: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      selectedMediaIds: []
    }
  },

  computed: {
    ...mapGetters('accounting/movement', ['getMovementOrderItemMedia']),

    orderMedia() {
      const data = this.getMovementOrderItemMedia(this.item.id)
      return {
        items: data?.media || [],
        loading: data?.loading || false,
        pagination: data?.pagination || null
      }
    },

    hasMedia() {
      return (this.item.media_ids && this.item.media_ids.length > 0) ||
        (this.item.medias_count > 0) ||
        this.orderMedia.items.length > 0
    },

    hasSelectedWithStatus() {
      return this.selectedMediaIds.some(id =>
        this.removedPhotoIds.includes(id)
      )
    },

    contextActions() {
      const actions = []

      if (this.canManagePhotos) {
        actions.push({
          value: 'remove',
          label: this.$pgettext('accounting.orderItem', 'Remove photos'),
          icon: 'cil-trash'
        })
        actions.push({
          value: 'clear-status',
          label: this.$pgettext('accounting.orderItem', 'Clear status'),
          icon: 'cil-x-circle'
        })
      }

      return actions
    }
  },

  mounted() {
    this.fetchOrderItemMedia({orderItemId: this.item.id})
  },

  methods: {
    ...mapActions('accounting/movement', [
      'fetchOrderItemMedia',
      'fetchMoreOrderItemMedia'
    ]),

    loadMore() {
      this.fetchMoreOrderItemMedia({orderItemId: this.item.id})
    },

    onAction({action, items}) {
      if (action === 'remove') {
        this.$emit('remove-photos', items)
        this.selectedMediaIds = []
      } else if (action === 'clear-status') {
        const photoIds = items.map(i => i.id).filter(id =>
          this.removedPhotoIds.includes(id)
        )
        if (photoIds.length > 0) {
          this.$emit('clear-status-photos', photoIds)
        }
        this.selectedMediaIds = []
      }
    },

    isRemoved(photoId) {
      return this.removedPhotoIds.includes(photoId)
    },

    removeSelected() {
      const items = this.orderMedia.items.filter(i => this.selectedMediaIds.includes(i.id))
      this.$emit('remove-photos', items)
      this.selectedMediaIds = []
    },

    clearStatusSelected() {
      const photoIds = this.selectedMediaIds.filter(id =>
        this.removedPhotoIds.includes(id)
      )
      this.$emit('clear-status-photos', photoIds)
      this.selectedMediaIds = []
    }
  }
}
</script>

<style scoped>
.photo-status-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.photo-status-remove {
  background: #ef4444;
  color: white;
}
</style>
